import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

/** Use this url for challenge related api slices */
const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_CHALLENGE_BASE_URL;

export type APIResponse = {
  httpStatus: string;
  statusCode: number;
};

export interface GetEnumTypeResponse extends APIResponse {
  response: string[];
}

export interface GetBaseComponentForLanguageResponse extends APIResponse {
  response: {
    baseComponent: string;
    userTemplate: string;
    id: string;
    language: string;
  };
}

export interface ProfileResponseType {
  statusCode: number;
  httpStatus: string;
  response: profileData;
}

export interface Category {
  id: string;
  name: string;
  icon: string | null;
  solvedQuestions: number;
  points: number;
  winRate: number;
  awards: number;
}

export interface profileData {
  name: string;
  rewards: string | null;
  createdOn: string;
  bio: string | null;
  profile?: string | null;
  profilePhoto?: string | null;
  categories: Category[];
  phoneNumber?: string;
  gender?: string;
  superSetId?: string;
}

export interface AssessmentSummaryType {
  statusCode: number;
  httpStatus: string;
  response: AssessmentSummary[];
}

export interface AssessmentSummary {
  challengeTitle: string;
  status: string;
  difficultyLevel: string;
  languages: string[];
  attempts: number;
  points: number;
  completedDate: string;
}

export interface EmailAvailabilityResponse {
  statusCode: number;
  httpStatus: string;
  response: boolean;
}

export const CommonApiSlice = createApi({
  reducerPath: "CommonApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getEnumType: builder.query<
      GetEnumTypeResponse,
      { enumType: string; category?: string }
    >({
      query: ({ enumType, category }) => ({
        url: `common/${enumType}`,
        method: "GET",
        params: category ? { category } : undefined,
      }),
    }),
    getBaseComponentForLanguage: builder.query<
      GetBaseComponentForLanguageResponse,
      { language: string }
    >({
      query: ({ language }) => ({
        url: `base-component?language=${language}`,
        method: "GET",
      }),
    }),
    getEmailAvailability: builder.query<
      EmailAvailabilityResponse,
      { email: string }
    >({
      query: ({ email }) => ({
        url: `user/check-email-exists?email=${email}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetEnumTypeQuery,
  useLazyGetEnumTypeQuery,
  useGetBaseComponentForLanguageQuery,
  useLazyGetBaseComponentForLanguageQuery,
  useLazyGetEmailAvailabilityQuery,
} = CommonApiSlice;

/** Challenge common api slice */
export const ChallengeCommonApiSlice = createApi({
  reducerPath: "ChallengeCommonApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  endpoints: (builder) => ({
    getChallengeEnumType: builder.query<
      GetEnumTypeResponse,
      { enumType: string }
    >({
      query: ({ enumType }) => ({
        url: `common/${enumType}`,
        method: "GET",
      }),
    }),
    getUserProfile: builder.query<ProfileResponseType, { userId: string }>({
      query: ({ userId }) => ({
        url: `challenge/profile-for-student?userId=${userId}`,
        method: "GET",
      }),
    }),
    getUserAssessmentSummary: builder.query<
      AssessmentSummaryType,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `challenge/get-challenge-history-profile?userId=${userId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetChallengeEnumTypeQuery,
  useLazyGetChallengeEnumTypeQuery,
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useGetUserAssessmentSummaryQuery,
} = ChallengeCommonApiSlice;
